/**
 * WhatsApp 인증 API
 * WhatsApp OTP 기반 로그인/로그아웃 및 상태 관리
 *
 * @see BE #297 - WhatsApp 휴대폰 인증 로그인
 * @see admin routes/customer-api.php - /api/v1/auth/whatsapp/* (canonical)
 */

import { apiClient } from './client';
import type {
  SendWhatsAppOtpRequest,
  SendWhatsAppOtpResponse,
  VerifyWhatsAppOtpRequest,
  VerifyWhatsAppOtpResponse,
  WhatsAppAuthStatusResponse,
  LogoutResponse,
} from './types';

/**
 * WhatsApp 인증 API 서비스
 */
export const whatsappAuthApi = {
  /**
   * WhatsApp OTP 발송
   * 입력된 전화번호로 6자리 인증 코드를 WhatsApp 메시지로 발송
   *
   * 1. Sanctum CSRF 쿠키 초기화
   * 2. 전화번호로 OTP 발송 요청
   * 3. 발송 결과 및 만료 시간 반환
   *
   * POST /api/v1/auth/whatsapp/send-otp
   *
   * @param phoneNumber E.164 형식 전화번호 (예: +521234567890)
   * @returns OTP 발송 결과 (성공 시 expires_at 포함)
   */
  sendOtp: async (phoneNumber: string, locale?: string): Promise<SendWhatsAppOtpResponse> => {
    // Sanctum SPA 인증을 위한 CSRF 쿠키 초기화
    await apiClient.initCsrf();

    const request: SendWhatsAppOtpRequest = { phone_number: phoneNumber, ...(locale ? { locale } : {}) };
    return apiClient.post<SendWhatsAppOtpResponse>('/api/v1/auth/whatsapp/send-otp', request);
  },

  /**
   * WhatsApp OTP 검증 및 로그인
   * 발송된 OTP 코드를 검증하고 사용자 인증 처리
   *
   * POST /api/v1/auth/whatsapp/verify-otp
   *
   * @param phoneNumber E.164 형식 전화번호
   * @param otp 6자리 인증 코드
   * @returns 검증 결과 (성공 시 user 정보 포함)
   */
  verifyOtp: async (phoneNumber: string, otp: string): Promise<VerifyWhatsAppOtpResponse> => {
    const request: VerifyWhatsAppOtpRequest = {
      phone_number: phoneNumber,
      otp,
    };
    return apiClient.post<VerifyWhatsAppOtpResponse>('/api/v1/auth/whatsapp/verify-otp', request);
  },

  /**
   * 인증 상태 확인
   * 현재 세션의 인증 상태 및 사용자 정보 조회
   *
   * GET /api/v1/auth/whatsapp/status
   *
   * @returns 인증 상태 (authenticated 여부, user 정보)
   */
  getStatus: async (): Promise<WhatsAppAuthStatusResponse> => {
    return apiClient.get<WhatsAppAuthStatusResponse>('/api/v1/auth/whatsapp/status');
  },

  /**
   * 로그아웃
   * 서버 세션 종료 및 CSRF 상태 리셋
   *
   * POST /api/v1/auth/whatsapp/logout
   *
   * @returns 로그아웃 결과
   */
  logout: async (): Promise<LogoutResponse> => {
    const response = await apiClient.post<LogoutResponse>('/api/v1/auth/whatsapp/logout');
    // CSRF 상태 리셋 (다음 로그인 시 재초기화)
    apiClient.resetCsrf();
    return response;
  },
};
